import { DecoratorNode, type LexicalNode, type SerializedLexicalNode } from "lexical";
import type { Component } from "vue";
import { h } from "vue";

import { useDataStore } from "~/stores";
import { isImageType } from "~/utils/media";

import Attachment from "./Attachment.vue";

export type SerializedAttachmentNode = SerializedLexicalNode & {
  attachmentDuid: string;
  collapsed: boolean;
  widthPx?: number | null;
};

export class AttachmentNode extends DecoratorNode<Component> {
  __attachmentDuid: string;

  __collapsed: boolean;

  __link: string = "";

  __name: string = "";

  __isImage: boolean = false;

  __widthPx: number | null;

  __initialized: boolean = false;

  static getType(): string {
    return "attachment";
  }

  static clone(node: AttachmentNode): AttachmentNode {
    return new AttachmentNode(node.__attachmentDuid, node.__collapsed, node.__key, node.__widthPx);
  }

  constructor(attachmentDuid: string, collapsed: boolean, key?: string, widthPx: number | null = null) {
    super(key);
    this.__attachmentDuid = attachmentDuid;
    this.__collapsed = collapsed;
    this.__widthPx = widthPx;
  }

  getAttachmentDuid(): string {
    return this.__attachmentDuid;
  }

  getCollapsed(): boolean {
    return this.__collapsed;
  }

  private initializeFromStore(): void {
    if (this.__initialized) {
      return;
    }
    const dataStore = useDataStore();
    const attachment = dataStore.getAttachmentByDuidIfLoaded(this.__attachmentDuid);
    this.__link = attachment?.fileUrl ?? "";
    this.__name = attachment?.name ?? "";
    this.__isImage = !!attachment?.kind && isImageType(attachment.kind);
    this.__initialized = true;
  }

  getLink(): string {
    this.initializeFromStore();
    return this.__link;
  }

  getName(): string {
    this.initializeFromStore();
    return this.__name;
  }

  getIsImage(): boolean {
    this.initializeFromStore();
    return this.__isImage;
  }

  getWidthPx(): number | null {
    return this.__widthPx;
  }

  setWidthPx(widthPx: number | null): void {
    const writable = this.getWritable();
    writable.__widthPx = widthPx;
  }

  setCollapsed(collapsed: boolean): void {
    const writable = this.getWritable();
    writable.__collapsed = collapsed;
  }

  // eslint-disable-next-line class-methods-use-this
  createDOM(): HTMLElement {
    const element = document.createElement("div");
    element.classList.add("contents");
    return element;
  }

  // eslint-disable-next-line class-methods-use-this
  updateDOM(): false {
    return false;
  }

  static importJSON(serializedNode: SerializedAttachmentNode): LexicalNode {
    return new AttachmentNode(
      serializedNode.attachmentDuid,
      serializedNode.collapsed,
      undefined,
      serializedNode.widthPx ?? null
    );
  }

  exportJSON(): SerializedAttachmentNode {
    return {
      type: AttachmentNode.getType(),
      attachmentDuid: this.getAttachmentDuid(),
      collapsed: this.getCollapsed(),
      widthPx: this.getWidthPx() ?? undefined,
      version: 1,
    };
  }

  decorate(): Component {
    return h(Attachment, {
      attachmentDuid: this.getAttachmentDuid(),
      collapsed: this.getCollapsed(),
      nodeKey: this.getKey(),
      widthPx: this.getWidthPx(),
      // eslint-disable-next-line no-use-before-define
      isAttachmentNode: $isAttachmentNode,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  isInline(): true {
    return true;
  }

  // eslint-disable-next-line class-methods-use-this
  isKeyboardSelectable(): false {
    return false;
  }

  getTextContent(): string {
    if (this.getIsImage()) {
      const width = this.getWidthPx();
      const styles = ["max-width: 100%", "height: auto", "display: inline-block"];
      if (width !== null) {
        styles.push(`width: ${width}px`);
      }
      return `<img src="${this.getLink()}" alt="${this.getName()}" style="${styles.join("; ")}" />`;
    }
    return `[${this.getName()}](${this.getLink()})`;
  }
}

export function $createAttachmentNode(
  attachmentDuid: string,
  collapsed: boolean,
  key?: string,
  widthPx: number | null = null
): AttachmentNode {
  return new AttachmentNode(attachmentDuid, collapsed, key, widthPx);
}

export function $isAttachmentNode(node?: LexicalNode): node is AttachmentNode {
  return node instanceof AttachmentNode;
}
